import {
  Link,
  Text,
} from '@chakra-ui/react';
import { currentDatabaseSelector, selectedDatabaseIdState } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import Chart from 'react-apexcharts';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { useInfrastructureRiskRating } from '@src/modules/performance/context/reports-api-hooks';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Road Assessment Rating',
  content: [
    {
      title: 'Infrastructure Risk Rating',
      description:
  <>
    <Text fontSize="inherit" mb={2}>
      As described in the
      {' '}
      <Link href="https://www.nzta.govt.nz/resources/infrastructure-risk-rating-manual" isExternal>IRR manual</Link>
      {' '}
      , IRR is a NZTA-developed methodology for assessing road safety risk. IRR ranks the underlying risk of each road section using five bands: High, Medium High, Medium, Low Medium, and Low.
    </Text>
    <Text fontSize="inherit" mb={2}>
      The ranking reflects the level of risk a road presents to an individual road user based on key physical and operational attributes.
    </Text>
    <Text fontSize="inherit">
      The chart shows the proportions (as percentages) of the chosen Infrastructure Risk Rating (IRR) bands that are allocated across the selected network compared to the proportions for the peer group (if any) and the National proportions.
    </Text>
  </>,
      source: <Text fontSize="inherit" as="span">NZTA</Text>,
    },
  ],
};

const IRR_BAND_ORDER = [
  'High',
  'Medium High',
  'Medium',
  'Low Medium',
  'Low',
];

function RoadSafetyRoadAssessmentRating() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data, isLoading } = useInfrastructureRiskRating();
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  // Transform data into groups for easier access when building chart series
  const groupedData = data?.reduce((acc, item) => {
    if (!acc[item.series]) acc[item.series] = {};
    acc[item.series][item.irrBand] = item.lengthPerc;
    return acc;
  }, {});

  const series = [
    {
      name: currentDatabase?.name,
      color: CHART_COLOURS.blue,
      data: IRR_BAND_ORDER.map((band) => ({
        x: band,
        y: groupedData?.Council?.[band] !== undefined
          ? Number(groupedData.Council[band].toFixed(2))
          : 0,
      })),
    },
    {
      name: 'Peer Group',
      color: CHART_COLOURS.black,
      data: IRR_BAND_ORDER.map((band) => ({
        x: band,
        y: groupedData?.PeerGroup?.[band] !== undefined
          ? Number(groupedData.PeerGroup[band].toFixed(2))
          : 0,
      })),
    },
    ...(!isNational
      ? [
        {
          name: 'National',
          color: CHART_COLOURS.lightgrey,
          data: IRR_BAND_ORDER.map((band) => ({
            x: band,
            y: groupedData?.National?.[band] !== undefined
              ? Number(groupedData.National[band].toFixed(2))
              : 0,
          })),
        },
      ]
      : []),
  ];

  // If all values for the Council series are 0, it indicates that there is no data for the selected RCA.
  // In this case, we return an empty series to show the "No data available" message in the chart.
  const filteredSeries = Object.values(groupedData?.Council || {}).every((v) => v === 0) ? [] : filterChartDataByName(
    series,
    chartFilters,
  );

  return (
    <ReportSection
      title="Road Assessment Rating"
      informationSource={informationSource}
      isLoading={isLoading || !data}
      lockYear={data?.[0]?.lockYear}
      links={[
        {
          label: 'ONF Safe Travel - Infrastructure Risk Rating',
          url: '/onf/transport-outcomes/hsp5',
          isExternal: false,
          userRoles: null,
        },
      ]}
    >
      <Chart
        options={{
          title: {
            text: 'Infrastructure Risk Rating',
          },
          chart: {
            type: 'bar',
            stacked: false,
            toolbar: {
              show: true,
            },
            zoom: {
              enabled: false,
            },
          },
          dataLabels: {
            enabled: false,
          },
          stroke: {
            show: false,
          },
          xaxis: {
            type: 'category',
            title: {
              text: '',
            },
          },
          yaxis: {
            min: 0,
            title: {
              text: 'Percentage (%)',
            },
            labels: {
              formatter: (val) => `${val.toFixed(0)}%`,
            },
          },
          tooltip: {
            shared: true,
            intersect: false,
            y: {
              formatter: (val) => `${val.toFixed(2)}%`,
            },
          },
        }}
        series={filteredSeries}
        type="bar"
        height={300}
      />
    </ReportSection>
  );
}

export default RoadSafetyRoadAssessmentRating;
